import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Activity, Mail, Lock, User, Eye, EyeOff, AlertCircle, Shield, Gift } from 'lucide-react';
import { toast } from 'sonner';
import { useSEO } from '../utils/seo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Signup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [trialDays, setTrialDays] = useState(null);
  
  // Fetch trial days from API
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await fetch(`${API_URL}/api/pricing`);
        const data = await response.json();
        setTrialDays(data.trial_days || 15);
      } catch (error) {
        setTrialDays(15); // Default to 15 if fetch fails
      }
    };
    fetchPricing();
  }, []);
  
  // SEO for signup page
  useSEO({
    title: `Start Free Trial | ${trialDays || 15} Days Free Sports Predictions`,
    description: `Sign up for SportsAnalysisAB and get ${trialDays || 15} days free access to AI-powered sports predictions. NHL, NBA, NFL, MLB picks with 60-80% accuracy. No credit card required.`,
    keywords: 'free sports picks, sports predictions free trial, sports betting tips free, NHL predictions free, NBA predictions free',
    canonicalUrl: 'https://sportsanalysisab.ca/signup'
  });

  // Pre-fill referral code from URL param
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref.toUpperCase());
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!ageConfirmed) {
      setError('You must confirm you are 18 years or older');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await register(email, password, name, ageConfirmed, referralCode);
      toast.success(`Account created with ${trialDays || 15}-day free trial! Please check your email to verify.`);
      navigate('/login');
    } catch (err) {
      setError(err.message || 'Registration failed');
      toast.error(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-sm bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
            <Activity className="w-7 h-7 text-white" />
          </div>
          <span className="font-display text-2xl font-bold tracking-tight uppercase">
            SportsAnalysisAB
          </span>
        </Link>

        {/* Card */}
        <div className="bg-card border border-border rounded-sm p-8">
          <h1 className="font-display text-2xl font-bold uppercase tracking-tight text-center mb-2">
            Create Account
          </h1>
          <p className="text-sm text-muted-foreground text-center mb-8">
            Join thousands of sports fans using data-driven insights
          </p>

          {/* Error message */}
          {error && (
            <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-sm p-3 mb-6">
              <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
              <span className="text-sm text-red-400">{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10 rounded-sm"
                  required
                  data-testid="signup-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 rounded-sm"
                  required
                  data-testid="signup-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 rounded-sm"
                  required
                  minLength={8}
                  data-testid="signup-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 rounded-sm"
                  required
                  data-testid="signup-confirm-password-input"
                />
              </div>
            </div>

            {/* Referral Code */}
            <div className="space-y-2">
              <Label htmlFor="referralCode">Referral Code (optional)</Label>
              <div className="relative">
                <Gift className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="referralCode"
                  type="text"
                  placeholder="Enter referral code"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  className="pl-10 rounded-sm uppercase"
                  data-testid="signup-referral-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Have a friend's referral code? Enter it to get bonus credits!
              </p>
            </div>

            {/* Age confirmation */}
            <div className="flex items-start gap-3 p-4 bg-secondary/50 rounded-sm">
              <Checkbox
                id="age-confirm"
                checked={ageConfirmed}
                onCheckedChange={setAgeConfirmed}
                className="mt-0.5"
                data-testid="age-confirm-checkbox"
              />
              <div>
                <Label htmlFor="age-confirm" className="text-sm font-medium cursor-pointer">
                  I confirm I am 18 years or older
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  This service is for adults only. By checking this box, you confirm you are of legal age.
                </p>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full btn-blaze rounded-sm font-bold uppercase tracking-wider"
              disabled={loading || !ageConfirmed}
              data-testid="signup-submit-btn"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          {/* Login link */}
          <p className="text-sm text-muted-foreground text-center mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-foreground hover:underline font-medium">
              Sign in
            </Link>
          </p>
        </div>

        {/* Disclaimer */}
        <div className="flex items-center justify-center gap-2 mt-6 text-xs text-muted-foreground">
          <Shield className="w-4 h-4" />
          <span>For entertainment purposes only. Not betting advice.</span>
        </div>
      </div>
    </div>
  );
}
